import { useEffect, useRef, useState } from 'react';
import { Search, ArrowRight, Building2, Home, CreditCard, Receipt, User as UserIcon, Plus, Settings, BarChart3, Webhook, Package as PackageIcon, Tag, Ticket, FileText, Wrench, Banknote, Globe, ClipboardList, Activity, Users as UsersIcon, Store } from 'lucide-react';
import { Link, useNavigate } from '@tanstack/react-router';
import { cn } from '../lib/cn';

type Command = {
  id: string;
  label: string;
  hint?: string;
  group: 'Navigate' | 'Account' | 'Admin' | 'Reseller' | 'Help';
  to: string;
  icon: React.ReactNode;
  keywords?: string;
};

const COMMANDS: Command[] = [
  // Owner
  { id: 'home', label: 'Home', group: 'Navigate', to: '/', icon: <Home className="w-4 h-4" />, keywords: 'dashboard overview' },
  { id: 'businesses', label: 'My businesses', group: 'Navigate', to: '/businesses', icon: <Building2 className="w-4 h-4" /> },
  { id: 'new-business', label: 'New business', hint: 'Create a new POS instance', group: 'Account', to: '/businesses/new', icon: <Plus className="w-4 h-4" /> },
  { id: 'subscription', label: 'Subscription', group: 'Account', to: '/subscription', icon: <CreditCard className="w-4 h-4" /> },
  { id: 'change-plan', label: 'Change plan', group: 'Account', to: '/change-plan', icon: <CreditCard className="w-4 h-4" />, keywords: 'upgrade downgrade pro basic starter' },
  { id: 'billing', label: 'Billing & invoices', group: 'Navigate', to: '/billing', icon: <Receipt className="w-4 h-4" /> },
  { id: 'team', label: 'Team', group: 'Account', to: '/team', icon: <UsersIcon className="w-4 h-4" />, keywords: 'invite cashier staff manager' },
  { id: 'add-ons', label: 'Manage add-ons', group: 'Account', to: '/add-ons', icon: <Tag className="w-4 h-4" />, keywords: 'extra location user products' },
  { id: 'profile', label: 'Profile & security', group: 'Account', to: '/profile', icon: <UserIcon className="w-4 h-4" />, keywords: 'password 2fa mfa email phone' },

  // Admin
  { id: 'admin-home', label: 'Platform overview', group: 'Admin', to: '/admin', icon: <BarChart3 className="w-4 h-4" />, keywords: 'kpi mrr' },
  { id: 'admin-resellers', label: 'Resellers', group: 'Admin', to: '/admin/resellers', icon: <Building2 className="w-4 h-4" /> },
  { id: 'admin-businesses', label: 'All businesses', group: 'Admin', to: '/admin/businesses', icon: <Store className="w-4 h-4" /> },
  { id: 'admin-packages', label: 'Packages', group: 'Admin', to: '/admin/packages', icon: <PackageIcon className="w-4 h-4" />, keywords: 'plan pricing product' },
  { id: 'admin-addons', label: 'Add-ons (catalog)', group: 'Admin', to: '/admin/addons', icon: <Tag className="w-4 h-4" /> },
  { id: 'admin-coupons', label: 'Coupons', group: 'Admin', to: '/admin/coupons', icon: <Ticket className="w-4 h-4" />, keywords: 'discount promo' },
  { id: 'admin-cash', label: 'Record cash payment', group: 'Admin', to: '/admin/cash-entry', icon: <Banknote className="w-4 h-4" /> },
  { id: 'admin-billing-flow', label: 'How billing works', group: 'Help', to: '/admin/billing-flow', icon: <CreditCard className="w-4 h-4" />, keywords: 'invoice payment sslcommerz dunning commission webhook flow reference' },
  { id: 'admin-gateways', label: 'Payment gateways', group: 'Admin', to: '/admin/gateways', icon: <Wrench className="w-4 h-4" />, keywords: 'sslcommerz stripe paypal' },
  { id: 'admin-domains', label: 'Domains', group: 'Admin', to: '/admin/domains', icon: <Globe className="w-4 h-4" /> },
  { id: 'admin-webhooks', label: 'Webhooks', group: 'Admin', to: '/admin/webhooks', icon: <Webhook className="w-4 h-4" /> },
  { id: 'admin-audit', label: 'Audit log', group: 'Admin', to: '/admin/audit', icon: <ClipboardList className="w-4 h-4" /> },
  { id: 'admin-health', label: 'System health', group: 'Admin', to: '/admin/health', icon: <Activity className="w-4 h-4" /> },
  { id: 'admin-settings', label: 'Platform settings', group: 'Admin', to: '/admin/settings', icon: <Settings className="w-4 h-4" /> },

  // Reseller
  { id: 'r-home', label: 'Reseller overview', group: 'Reseller', to: '/reseller', icon: <BarChart3 className="w-4 h-4" /> },
  { id: 'r-customers', label: 'Reseller customers', group: 'Reseller', to: '/reseller/customers', icon: <Store className="w-4 h-4" /> },
  { id: 'r-staff', label: 'Reseller staff', group: 'Reseller', to: '/reseller/staff', icon: <UsersIcon className="w-4 h-4" /> },
  { id: 'r-revenue', label: 'Reseller revenue', group: 'Reseller', to: '/reseller/revenue', icon: <BarChart3 className="w-4 h-4" /> },
  { id: 'r-payments', label: 'Reseller customer payments', group: 'Reseller', to: '/reseller/payments', icon: <Receipt className="w-4 h-4" />, keywords: 'commission gross net invoice' },
  { id: 'r-packages', label: 'My packages (reseller)', group: 'Reseller', to: '/reseller/packages', icon: <PackageIcon className="w-4 h-4" />, keywords: 'pricing catalog tier' },
  { id: 'r-branding', label: 'Reseller branding', group: 'Reseller', to: '/reseller/branding', icon: <FileText className="w-4 h-4" /> },

  // Help
  { id: 'h-pricing', label: 'View pricing page', group: 'Help', to: '/pricing', icon: <CreditCard className="w-4 h-4" /> },
];

/**
 * ⌘K command palette. Searchable list of every navigable destination.
 * Mounted globally; opened by ⌘K / Ctrl+K, closed by Esc, click-outside, or selection.
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Reset state on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 10);
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
    return undefined;
  }, [open]);

  // Esc to close
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const q = query.trim().toLowerCase();
  const filtered = q
    ? COMMANDS.filter((c) => {
        const hay = `${c.label} ${c.group} ${c.hint ?? ''} ${c.keywords ?? ''}`.toLowerCase();
        return hay.includes(q);
      })
    : COMMANDS;

  const grouped = filtered.reduce<Record<string, Command[]>>((acc, c) => {
    if (!acc[c.group]) acc[c.group] = [];
    acc[c.group]!.push(c);
    return acc;
  }, {});

  function onListKey(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      const target = filtered[active];
      if (target) {
        navigate({ to: target.to });
        onClose();
      }
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="fixed inset-0 z-[60] flex items-start justify-center pt-[12vh] px-4"
    >
      <div
        className="absolute inset-0 bg-zinc-950/40 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={cn(
          'relative w-full max-w-xl rounded-xl bg-white dark:bg-zinc-900 shadow-lg overflow-hidden',
          'border border-zinc-200 dark:border-zinc-700',
        )}
        onKeyDown={onListKey}
      >
        <div className="flex items-center gap-3 px-4 border-b border-zinc-200 dark:border-zinc-700">
          <Search className="w-4 h-4 text-zinc-500 dark:text-zinc-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            placeholder="Search pages, settings, actions…"
            className="flex-1 py-3.5 bg-transparent border-0 outline-none text-sm text-zinc-950 dark:text-zinc-50 placeholder-zinc-400 dark:placeholder-zinc-500"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="hidden sm:inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-700">
            esc
          </kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto nice-scroll py-2">
          {filtered.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">
              Nothing matched "<span className="font-medium">{query}</span>". Try fewer words.
            </div>
          ) : (
            Object.entries(grouped).map(([group, items]) => (
              <div key={group} className="py-1">
                <div className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                  {group}
                </div>
                {items.map((c) => {
                  const idx = filtered.indexOf(c);
                  const isActive = idx === active;
                  return (
                    <Link
                      key={c.id}
                      to={c.to as never}
                      onClick={onClose}
                      onMouseEnter={() => setActive(idx)}
                      className={cn(
                        'flex items-center gap-3 px-4 py-2.5 transition cursor-pointer',
                        isActive
                          ? 'bg-brand-subtle dark:bg-brand-dm-subtle/50'
                          : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/50',
                      )}
                    >
                      <span
                        className={cn(
                          'w-7 h-7 grid place-items-center rounded-md flex-shrink-0',
                          isActive
                            ? 'bg-white dark:bg-zinc-900 text-brand dark:text-brand-dm'
                            : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400',
                        )}
                      >
                        {c.icon}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-zinc-950 dark:text-zinc-50 truncate">
                          {c.label}
                        </div>
                        {c.hint && (
                          <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{c.hint}</div>
                        )}
                      </div>
                      {isActive && <ArrowRight className="w-3.5 h-3.5 text-zinc-400 dark:text-zinc-500 flex-shrink-0" />}
                    </Link>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="px-4 py-2.5 border-t border-zinc-200 dark:border-zinc-700 bg-zinc-50/50 dark:bg-zinc-900/50 flex items-center justify-between text-[11px] text-zinc-500 dark:text-zinc-400">
          <span className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <Kbd>↑</Kbd>
              <Kbd>↓</Kbd> navigate
            </span>
            <span className="inline-flex items-center gap-1">
              <Kbd>↵</Kbd> open
            </span>
          </span>
          <span className="hidden sm:flex items-center gap-1">Toggle <Kbd>⌘</Kbd><Kbd>K</Kbd></span>
        </div>
      </div>
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-white dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700">
      {children}
    </kbd>
  );
}
